// SfxApi on WebAudio. Pooled voices with a hard voice limit and priority stealing; spatial
// voices through equal-power PannerNodes with distance air absorption (a per-voice low-pass
// set once at start, never per frame) and a send into one shared outdoor reverb; round-robin
// variations so a repeated sound never plays the same take twice running; a sfx dip and a
// "thump" low-pass for big hits; Pop's voice; and the music director (music.ts) streaming
// the Godot score.
//
// Files are lazy-loaded after unlock() (first user gesture) and never block a frame: a play()
// of a sound that hasn't decoded yet, or of a name that isn't in the bank, is simply dropped.
// web/public/audio/bank.json (written by tools/audio/build_audio.py) lists the variations.
//
// Bus graph:
//   spatial voice: src > gain > lowpass > panner > sfxBus ─┐            ┌> reverb send
//   flat voice:    src > gain > lowpass ─────────> sfxBus ─┤            │
//   sfxBus > dip > thump lowpass ──────────────────────────┴> comp > master > out
//   uiBus (hit markers, menus, Pop) ──────────────────────────> comp          (never dipped)
//   musicBus (music.ts decks + ambience beds) ────────────────────> master

import type * as THREE from 'three';
import type { SfxApi } from '../render/contracts';
import { SYNTH, synthBuffer } from './synth';
import { SYNTH_MORE, outdoorIR, synthMore } from './synth_more';
import { Music, type MusicState } from './music';
import { PopVoice, type VoiceTag } from './voice';

export type { MusicState } from './music';
export type { VoiceTag } from './voice';

interface Def {
  /** file stem in /audio/ (mp3); variations come from bank.json. Omitted = the bank name. */
  file?: string;
  vol: number;
  /** base playback rate */
  rate?: number;
  /** random pitch spread (±) */
  pv?: number;
  /** max simultaneous voices of this sound */
  max?: number;
  /** min seconds between two starts */
  gap?: number;
  /** 0 low .. 3 critical: who wins when voices run out */
  pri?: number;
  /** HUD-ish: never spatial, never dipped */
  ui?: boolean;
  /** reverb send 0..1 (default 0.12; guns and booms want more) */
  wet?: number;
  /** also play this bank entry (layering), optionally after `layerDelay` s */
  layer?: string;
  layerDelay?: number;
}

const GUN = { pv: 0.035, max: 4, pri: 2, wet: 0.35 };
const AUTO = { pv: 0.04, max: 6, gap: 0.03, pri: 2, wet: 0.3 };

/** The bank. Names are the contract with the game client (and the other lanes). */
export const BANK: Record<string, Def> = {
  // ------------------------------------------------ legacy names (first web pass), kept working
  shot_gunslinger: { file: 'shot_gunslinger', vol: 0.85, pv: 0.04, max: 4, pri: 2, wet: 0.3 },
  shot_valkyrie: { file: 'shot_valkyrie', vol: 0.55, pv: 0.06, max: 6, gap: 0.03, pri: 2, wet: 0.25 },
  shot_phantom: { file: 'shot_phantom', vol: 0.6, pv: 0.05, max: 4, pri: 2, wet: 0.25 },
  shot_juggernaut: { file: 'shot_juggernaut', vol: 0.55, pv: 0.07, max: 6, gap: 0.03, pri: 2, wet: 0.25 },
  reload: { file: 'reload', vol: 0.6, pv: 0.03, max: 2, pri: 2 },
  reload_round: { file: 'reload_round', vol: 0.55, pv: 0.06, max: 2, pri: 2 },
  empty: { file: 'dry_fire', vol: 0.55, max: 1, gap: 0.08, pri: 2 },
  melee: { file: 'melee_swing', vol: 0.7, pv: 0.06, max: 2, pri: 2 },
  hit: { file: 'hit', vol: 0.45, pv: 0.05, max: 3, gap: 0.035, pri: 3, ui: true },
  headshot: { file: 'headshot', vol: 0.7, pv: 0.03, max: 2, gap: 0.04, pri: 3, ui: true },
  kill: { file: 'kill', vol: 0.65, max: 2, gap: 0.05, pri: 3, ui: true },
  armor: { file: 'impact_armor', vol: 0.5, pv: 0.1, max: 3, gap: 0.04, pri: 1 },
  shield_hit: { file: 'impact_shield', vol: 0.45, pv: 0.08, max: 3, gap: 0.04, pri: 1 },
  prime: { file: 'prime', vol: 0.75, pv: 0.03, max: 3, pri: 3 },
  detonate: { file: 'detonate', vol: 1.0, pv: 0.04, max: 3, pri: 3, wet: 0.4, layer: 'explosion_l' },
  explosion: { file: 'explosion_m', vol: 0.9, pv: 0.06, max: 4, pri: 3, wet: 0.45 },
  enemy_shoot: { file: 'shooter_fire', vol: 0.42, pv: 0.08, max: 5, gap: 0.04, pri: 1, wet: 0.25 },
  sniper_charge: { file: 'sniper_tell', vol: 0.75, max: 2, pri: 3 },
  sniper_fire: { file: 'sniper_shot', vol: 0.85, pv: 0.04, max: 2, pri: 3, wet: 0.45 },
  bomb_throw: { file: 'grenade_throw', vol: 0.6, pv: 0.08, max: 3, pri: 2 },
  toaster_pop: { vol: 0.5, pv: 0.12, max: 4, gap: 0.05, pri: 2 },
  brute_roar: { file: 'brute_roar', vol: 0.8, pv: 0.06, max: 2, pri: 2, wet: 0.3 },
  dodge: { file: 'roll', vol: 0.55, pv: 0.06, max: 2, pri: 2 },
  land: { vol: 0.5, pv: 0.1, max: 2, gap: 0.1, pri: 1 },
  jump: { file: 'jump_push', vol: 0.45, pv: 0.06, max: 2, pri: 1 },
  hurt: { file: 'player_hit', vol: 0.7, pv: 0.05, max: 2, gap: 0.08, pri: 3, ui: true },
  shield_break: { file: 'shield_down', vol: 0.8, max: 1, pri: 3, ui: true },
  down: { file: 'player_down', vol: 0.85, max: 2, pri: 3 },
  revive: { file: 'revive', vol: 0.8, max: 2, pri: 3 },
  wave_start: { file: 'order_start', vol: 0.7, max: 1, pri: 3, ui: true },
  wave_clear: { file: 'order_done', vol: 0.75, max: 1, pri: 3, ui: true },
  notice: { file: 'objective_up', vol: 0.5, max: 1, gap: 0.2, pri: 2, ui: true },
  ui_move: { file: 'ui_move', vol: 0.4, max: 2, gap: 0.03, pri: 3, ui: true },
  ui_select: { file: 'ui_select', vol: 0.5, max: 2, pri: 3, ui: true },
  overheat: { file: 'overheat', vol: 0.7, max: 1, gap: 0.3, pri: 3, ui: true },
  denied: { file: 'ui_refuse', vol: 0.45, max: 1, gap: 0.12, pri: 3, ui: true },
  spawn_warn: { file: 'spawn_in', vol: 0.55, pv: 0.05, max: 3, pri: 2 },
  robot_die: { file: 'robot_die', vol: 0.7, pv: 0.1, max: 4, pri: 2, layer: 'robot_debris' },
  robot_debris: { file: 'debris_clatter', vol: 0.4, pv: 0.12, max: 3, pri: 1 },
  // the run lane (PR #50) and the saloon lane (PR #51) use these names
  warp: { file: 'warp_go', vol: 0.85, max: 1, pri: 3, ui: true, layer: 'warp_flash' },
  island_cleared: { file: 'island_done', vol: 0.8, max: 1, pri: 3, ui: true },
  objective: { file: 'objective_up', vol: 0.55, max: 1, gap: 0.3, pri: 3, ui: true },
  powerup_pickup: { file: 'pu_pickup', vol: 0.75, max: 2, gap: 0.08, pri: 3, ui: true },
  powerup_use: { file: 'pu_use', vol: 0.85, max: 1, pri: 3, ui: true, layer: 'el_shock' },
  powerup_denied: { file: 'pu_denied', vol: 0.6, max: 1, gap: 0.3, pri: 2, ui: true },
  boss_intro: { file: 'boss_sting', vol: 0.9, max: 1, pri: 3, ui: true },
  boss_phase: { file: 'boss_phase2', vol: 0.85, max: 1, pri: 3, ui: true },
  cerberus_roar: { file: 'cerberus_roar2', vol: 1.0, pv: 0.05, max: 2, pri: 3, wet: 0.5 },
  extract_sting: { file: 'recall_sting', vol: 0.8, max: 1, pri: 3, ui: true },
  // the whiskey heal (synth_more.ts): the cork, three swallows, the empty smashing where it's tossed
  whiskey_uncork: { vol: 0.55, pv: 0.05, max: 2, gap: 0.1, pri: 2 },
  whiskey_gulp: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 2 },
  glass_shatter: { vol: 0.8, pv: 0.07, max: 3, gap: 0.05, pri: 2, wet: 0.35 },
  bell: { file: 'hand_bell', vol: 1.0, pv: 0.02, max: 2, gap: 0.12, pri: 3, wet: 0.6 },
  moo: { file: 'cow_moo', vol: 0.75, pv: 0.08, max: 2, gap: 0.6, pri: 1, wet: 0.35 },
  moo_bull: { file: 'bull_moo', vol: 0.85, pv: 0.06, max: 2, gap: 0.6, pri: 1, wet: 0.35 },
  jetpack_ignite: { file: 'jet_ignite', vol: 0.9, max: 2, pri: 3 },
  hotdog_slide: { file: 'hotdog', vol: 0.8, max: 1, pri: 2 },
  roof_timber: { file: 'roof_break', vol: 0.9, max: 1, pri: 3, wet: 0.3 },
  service_klaxon: { file: 'klaxon', vol: 0.75, max: 1, pri: 3 },

  // ------------------------------------------------ guns (Shooting feel lane plays shot_<W id>)
  shot_0: { vol: 0.7, rate: 1.0, ...GUN },
  shot_1: { vol: 0.6, ...AUTO },
  shot_2: { vol: 0.6, ...AUTO },
  shot_3: { vol: 1.0, ...GUN, wet: 0.55, layer: 'bolt_cycle', layerDelay: 0.42 },
  shot_4: { vol: 0.95, ...GUN, wet: 0.45 },
  shot_5: { vol: 0.95, ...GUN, wet: 0.5 },
  shot_6: { vol: 0.7, ...GUN },
  shot_7: { vol: 0.6, ...AUTO },
  shot_8: { vol: 0.95, ...GUN, wet: 0.45 },
  bolt_cycle: { vol: 0.45, pv: 0.03, max: 2, pri: 1 },
  turret_fire: { vol: 0.4, pv: 0.06, max: 4, gap: 0.05, pri: 1, wet: 0.2 },
  dry_fire: { vol: 0.55, max: 1, gap: 0.08, pri: 2 },
  weapon_swap: { vol: 0.5, max: 1, pri: 2 },
  special_pickup: { vol: 0.7, max: 1, pri: 3, ui: true },
  blaster_charge: { vol: 0.5, max: 2, pri: 2 },
  blaster_full: { vol: 0.55, max: 2, pri: 2 },

  // ------------------------------------------------ impacts + confirms
  impact_metal: { vol: 0.42, pv: 0.08, max: 4, gap: 0.03, pri: 1 },
  impact_armor: { vol: 0.5, pv: 0.1, max: 3, gap: 0.04, pri: 1 },
  impact_shield: { vol: 0.45, pv: 0.08, max: 3, gap: 0.04, pri: 1 },
  impact_dirt: { vol: 0.35, pv: 0.1, max: 4, gap: 0.03, pri: 0 },
  impact_weak: { vol: 0.6, pv: 0.05, max: 2, gap: 0.05, pri: 2 },
  kill_head: { file: 'killmark', vol: 0.75, max: 2, gap: 0.05, pri: 3, ui: true, layer: 'crit' },
  kill_big: { vol: 0.8, max: 1, gap: 0.08, pri: 3, ui: true, wet: 0.2 },
  hitmark: { vol: 0.5, pv: 0.04, max: 3, gap: 0.035, pri: 3, ui: true },
  killmark: { vol: 0.7, max: 2, gap: 0.05, pri: 3, ui: true },
  crit: { vol: 0.55, max: 2, gap: 0.05, pri: 3, ui: true },
  shell_tink: { file: 'shell_tink', vol: 0.22, pv: 0.08, max: 6, gap: 0.02, pri: 0 },
  debris_clatter: { vol: 0.4, pv: 0.12, max: 3, pri: 1 },
  wreck_fizz: { file: 'wreck_fizz', vol: 0.3, pv: 0.15, max: 3, gap: 0.08, pri: 0 },
  bullet_whiz: { vol: 0.55, pv: 0.1, max: 3, gap: 0.06, pri: 2 },

  // ------------------------------------------------ explosions + grenades + elements
  explosion_s: { vol: 0.8, pv: 0.06, max: 4, pri: 3, wet: 0.45 },
  explosion_m: { vol: 0.9, pv: 0.06, max: 4, pri: 3, wet: 0.5 },
  explosion_l: { vol: 1.0, pv: 0.04, max: 3, pri: 3, wet: 0.6 },
  explosion_close: { vol: 0.9, pv: 0.05, max: 2, pri: 3, wet: 0.3 },
  stun_pop: { vol: 0.8, pv: 0.05, max: 3, pri: 3, wet: 0.4 },
  gas_pop: { vol: 0.6, pv: 0.05, max: 3, pri: 2, wet: 0.2 },
  pin_pull: { vol: 0.6, max: 2, pri: 2, ui: true },
  grenade_throw: { vol: 0.55, pv: 0.08, max: 3, pri: 2 },
  grenade_ready: { vol: 0.45, max: 1, pri: 2, ui: true },
  grenade_pickup: { vol: 0.55, max: 1, pri: 2, ui: true },
  detonation: { vol: 0.9, pv: 0.05, max: 3, pri: 3, wet: 0.5 },
  prime_on: { vol: 0.55, pv: 0.05, max: 3, gap: 0.04, pri: 2 },
  fuse_tick: { vol: 0.35, max: 2, gap: 0.05, pri: 2, ui: true },
  el_fire: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 1 },
  el_fire_tick: { vol: 0.25, pv: 0.1, max: 3, gap: 0.12, pri: 0 },
  el_ice: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 1 },
  el_freeze: { vol: 0.65, pv: 0.04, max: 2, gap: 0.1, pri: 2 },
  el_thaw: { vol: 0.4, max: 2, gap: 0.2, pri: 0 },
  el_shock: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 1 },
  el_arc: { vol: 0.5, pv: 0.08, max: 3, gap: 0.06, pri: 1 },
  el_acid: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 1 },
  el_acid_strip: { vol: 0.55, max: 2, gap: 0.2, pri: 2 },
  el_mag: { vol: 0.5, pv: 0.06, max: 3, gap: 0.08, pri: 1 },
  el_wet: { vol: 0.45, pv: 0.06, max: 3, gap: 0.1, pri: 1 },
  el_singularity: { vol: 0.7, max: 2, pri: 2, wet: 0.3 },
  el_burst: { vol: 0.75, max: 2, pri: 2, wet: 0.4 },
  hellfire: { vol: 0.6, max: 2, gap: 0.15, pri: 1 },

  // ------------------------------------------------ the cowboy
  jet_ignite: { vol: 0.75, pv: 0.04, max: 3, gap: 0.15, pri: 2 },
  jet_engage: { vol: 0.65, max: 2, gap: 0.3, pri: 2 },
  jet_cut: { vol: 0.5, pv: 0.05, max: 3, gap: 0.2, pri: 1 },
  jet_boost: { vol: 0.75, max: 2, gap: 0.2, pri: 2, wet: 0.2 },
  jet_bank: { vol: 0.45, max: 1, gap: 0.6, pri: 1 },
  jump_push: { vol: 0.45, pv: 0.06, max: 2, pri: 1 },
  roll: { vol: 0.55, pv: 0.06, max: 2, pri: 2 },
  dodge_whoosh: { vol: 0.5, pv: 0.06, max: 2, pri: 2 },
  slide: { vol: 0.5, pv: 0.06, max: 2, pri: 1 },
  melee_swing: { vol: 0.65, pv: 0.06, max: 2, pri: 2 },
  melee_hit: { vol: 0.75, pv: 0.06, max: 2, pri: 2 },
  melee_crit: { vol: 0.8, max: 2, pri: 3 },
  ground_pound: { vol: 0.9, max: 2, pri: 3, wet: 0.4 },
  warp_go: { vol: 0.8, max: 1, pri: 3, ui: true },
  warp_flash: { vol: 0.6, max: 1, pri: 3, ui: true },
  warp_arrive: { vol: 0.7, max: 1, pri: 3, ui: true },
  player_hit: { vol: 0.7, pv: 0.05, max: 2, gap: 0.08, pri: 3, ui: true },
  player_down: { vol: 0.85, max: 2, pri: 3 },
  player_respawn: { vol: 0.7, max: 1, pri: 3, ui: true },
  shield_down: { vol: 0.8, max: 1, pri: 3, ui: true },
  shield_wait: { vol: 0.45, max: 1, gap: 0.5, pri: 2, ui: true },
  heat_ignite: { vol: 0.6, max: 1, gap: 0.5, pri: 3, ui: true },
  cool_hiss: { vol: 0.45, max: 2, gap: 0.3, pri: 1 },
  stim: { vol: 0.6, max: 1, pri: 2, ui: true },
  damage_dir: { vol: 0.5, max: 2, gap: 0.08, pri: 3, ui: true },

  // ------------------------------------------------ robots (robot_die_<EType>)
  robot_die_0: { vol: 0.55, pv: 0.1, max: 4, pri: 2, layer: 'toaster_pop' },
  robot_die_1: { vol: 0.65, pv: 0.08, max: 4, pri: 2, layer: 'robot_debris' },
  robot_die_2: { vol: 0.85, pv: 0.06, max: 3, pri: 2, wet: 0.3, layer: 'robot_debris' },
  robot_die_3: { vol: 0.65, pv: 0.08, max: 3, pri: 2, layer: 'robot_debris' },
  robot_die_4: { vol: 0.7, pv: 0.06, max: 3, pri: 2, layer: 'explosion_s' },
  robot_die_5: { vol: 1.0, pv: 0.04, max: 2, pri: 3, wet: 0.5, layer: 'debris_clatter', layerDelay: 0.25 },
  robot_die_6: { vol: 0.65, pv: 0.06, max: 3, pri: 2, layer: 'wreck_fizz' },
  robot_die_7: { vol: 0.7, pv: 0.06, max: 3, pri: 2, layer: 'wreck_fizz' },
  robot_alert: { vol: 0.45, pv: 0.08, max: 3, gap: 0.25, pri: 1 },
  robot_stagger: { vol: 0.6, pv: 0.06, max: 2, gap: 0.1, pri: 2 },
  robot_knock: { vol: 0.5, pv: 0.08, max: 3, gap: 0.06, pri: 1 },
  spawn_in: { vol: 0.5, pv: 0.06, max: 4, gap: 0.1, pri: 1 },
  swarmer_alert: { vol: 0.45, pv: 0.1, max: 3, gap: 0.3, pri: 1 },
  swarmer_jump: { vol: 0.4, pv: 0.1, max: 3, gap: 0.1, pri: 1 },
  swarmer_slash: { vol: 0.6, pv: 0.08, max: 3, gap: 0.08, pri: 2 },
  swarmer_tell: { vol: 0.5, pv: 0.08, max: 3, gap: 0.1, pri: 2 },
  shooter_fire: { vol: 0.42, pv: 0.08, max: 5, gap: 0.04, pri: 1, wet: 0.25 },
  shooter_tell: { vol: 0.5, max: 3, gap: 0.15, pri: 2 },
  shooter_blast: { vol: 0.6, pv: 0.06, max: 3, pri: 2, wet: 0.3 },
  brute_tell: { vol: 0.75, max: 2, gap: 0.2, pri: 3 },
  brute_slam: { vol: 0.95, pv: 0.05, max: 2, pri: 3, wet: 0.4 },
  brute_charge: { vol: 0.75, max: 2, gap: 0.3, pri: 3 },
  brute_step: { vol: 0.5, pv: 0.08, max: 3, gap: 0.12, pri: 0 },
  sniper_tell: { vol: 0.75, max: 2, pri: 3 },
  sniper_shot: { vol: 0.85, pv: 0.04, max: 2, pri: 3, wet: 0.5 },
  bomber_lob: { vol: 0.6, pv: 0.08, max: 3, pri: 2 },
  bomber_up: { vol: 0.5, max: 2, gap: 0.3, pri: 1 },
  disruptor_throw: { vol: 0.6, pv: 0.06, max: 2, pri: 2 },
  disruptor_sparkle: { vol: 0.4, max: 2, gap: 0.3, pri: 0 },
  engineer_deploy: { vol: 0.65, max: 2, pri: 2 },
  engineer_gas: { vol: 0.6, max: 2, pri: 2 },
  engineer_work: { vol: 0.4, max: 2, gap: 0.5, pri: 0 },
  metal_tell: { vol: 0.85, max: 1, pri: 3 },
  metal_fire: { vol: 0.95, pv: 0.04, max: 2, pri: 3, wet: 0.45 },
  metal_warn: { vol: 0.75, max: 1, gap: 1, pri: 3 },
  cerberus_roar2: { vol: 1.0, pv: 0.05, max: 2, pri: 3, wet: 0.5 },
  boss_sting: { vol: 0.9, max: 1, pri: 3, ui: true },
  boss_phase2: { vol: 0.85, max: 1, pri: 3, ui: true },

  // ------------------------------------------------ run + UI + saloon
  ui_tick: { vol: 0.5, max: 2, gap: 0.03, pri: 3, ui: true },
  ui_back: { vol: 0.55, max: 2, gap: 0.05, pri: 3, ui: true },
  ui_refuse: { vol: 0.45, max: 1, gap: 0.12, pri: 3, ui: true },
  ui_open: { vol: 0.45, max: 1, gap: 0.1, pri: 3, ui: true },
  ui_pick: { vol: 0.55, max: 2, gap: 0.04, pri: 3, ui: true },
  ui_ready: { vol: 0.6, max: 1, pri: 3, ui: true },
  ui_start: { vol: 0.6, max: 1, pri: 3, ui: true },
  ride_out: { vol: 0.7, max: 1, pri: 3, ui: true },
  run_begin: { vol: 0.7, max: 1, pri: 3, ui: true },
  order_start: { vol: 0.7, max: 1, pri: 3, ui: true },
  order_done: { vol: 0.75, max: 1, pri: 3, ui: true },
  island_done: { vol: 0.8, max: 1, pri: 3, ui: true },
  objective_up: { vol: 0.5, max: 1, gap: 0.2, pri: 2, ui: true },
  death_screen: { vol: 0.7, max: 1, pri: 3, ui: true },
  respawn_tick: { vol: 0.5, max: 1, gap: 0.3, pri: 3, ui: true },
  recall_sting: { vol: 0.8, max: 1, pri: 3, ui: true },
  poster_pin: { vol: 0.6, max: 1, pri: 2, ui: true },
  squish: { vol: 0.7, max: 1, pri: 2 },
  harness_pop: { vol: 0.7, max: 1, pri: 2 },
  door_swing: { file: 'door_swing', vol: 0.8, pv: 0.06, max: 2, gap: 0.4, pri: 3, wet: 0.25 },
  stool_rise: { vol: 0.7, max: 1, pri: 2 },
  hatch_open: { vol: 0.8, max: 1, pri: 3 },
  arm_servo: { vol: 0.7, max: 2, pri: 2 },
  pack_clamp: { vol: 0.8, max: 2, pri: 2 },

  // ------------------------------------------------ loops (sfx.loop / sfx.jet)
  jetpack: { vol: 0.5, pri: 3 },
  jet_hover_loop: { vol: 0.55, pri: 3 },
  jet_thrust_loop: { vol: 0.6, pri: 3 },
  flight_wind: { vol: 0.55, pri: 3 },
  sniper_laser: { vol: 0.35, pri: 3 },
  bomber_loop: { vol: 0.4, pri: 1 },
  aura_loop: { vol: 0.3, pri: 1 },
  gas_loop: { vol: 0.35, pri: 1 },
  low_hp_loop: { vol: 0.45, pri: 3 },
  burn_loop: { vol: 0.4, pri: 2 },
  charge_loop: { vol: 0.4, pri: 2 },
  spawn_loop: { vol: 0.3, pri: 1 },
};
// reloads, rounds and equips per gun id (only the ones Godot has takes for)
for (const id of [1, 2, 4, 5, 7, 8]) BANK['reload_start_' + id] = { vol: 0.55, pv: 0.03, max: 2, pri: 2 };
for (const id of [1, 2, 3, 4, 5, 7, 8]) BANK['reload_end_' + id] = { vol: 0.6, pv: 0.03, max: 2, pri: 2 };
for (const id of [3, 4, 5, 8]) BANK['reload_round_' + id] = { vol: 0.5, pv: 0.06, max: 2, gap: 0.05, pri: 2 };
for (let id = 0; id <= 8; id++) BANK['equip_' + id] = { vol: 0.5, max: 1, pri: 2 };
for (const n of ['equip_special', 'equip_harpoon', 'equip_bigone']) BANK[n] = { vol: 0.5, max: 1, pri: 2 };
// Specials: rockets 9-14 + Bad Idea 17, harpoon 15, Big One 16, turret 18, fireworks 19-33
for (let id = 9; id <= 33; id++) {
  const d: Def =
    id <= 14 ? { file: 'shot_rocket', vol: 0.85, ...GUN, wet: 0.45 }
    : id === 15 ? { file: 'shot_harpoon', vol: 0.8, ...GUN }
    : id === 16 ? { file: 'shot_bigone', vol: 0.9, ...GUN, wet: 0.5 }
    : id === 17 ? { file: 'shot_rocket', vol: 0.95, rate: 0.82, ...GUN, wet: 0.5 }
    : id === 18 ? { file: 'engineer_deploy', vol: 0.7, max: 2, pri: 2 }
    : { file: 'shot_firework', vol: 0.75, ...GUN, wet: 0.4 };
  BANK['shot_' + id] = d;
}

interface Voice {
  src: AudioBufferSourceNode | null;
  gain: GainNode;
  filt: BiquadFilterNode;
  pan: PannerNode | null;
  send: GainNode | null;
  name: string;
  pri: number;
  start: number;
  end: number;
}

interface LoopVoice { src: AudioBufferSourceNode; gain: GainNode; pan: PannerNode | null; name: string; stopAt: number; vol: number; rate: number }
interface JetState { on: boolean; thr: number; lastIgnite: number; ref: number; refT: number; kh: string; kt: string; kb: string }

const SPATIAL_VOICES = 32;
const FLAT_VOICES = 14;
const MAX_HEAR = 140;

function baseUrl() {
  try { return ((import.meta as any).env?.BASE_URL ?? '/') + 'audio/'; } catch { return '/audio/'; }
}

/** Skip leading near-silence (mp3 encoder delay, a stray ms of lead-in). The build already
 *  trims the takes, so this only eats what is under -45 dB and never more than 30 ms. */
function onsetOf(buf: AudioBuffer) {
  const d = buf.getChannelData(0), n = Math.min(d.length, Math.floor(buf.sampleRate * 0.4));
  let pk = 0;
  for (let i = 0; i < n; i++) pk = Math.max(pk, Math.abs(d[i]));
  const thr = pk * 0.0056, cap = Math.floor(buf.sampleRate * 0.03);
  for (let i = 0; i < cap; i++) if (Math.abs(d[i]) > thr) return Math.max(0, i / buf.sampleRate - 0.001);
  return cap / buf.sampleRate;
}

export class Sfx implements SfxApi {
  ctx: AudioContext | null = null;
  readonly music = new Music();
  private voice: PopVoice | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private uiBus!: GainNode;
  private dipGain!: GainNode;
  private thumpFilt!: BiquadFilterNode;
  private musicBus!: GainNode;
  private ambBus!: GainNode;
  private reverbIn!: GainNode;
  private vars = new Map<string, AudioBuffer[]>();
  private onset = new WeakMap<AudioBuffer, number>();
  private lastVar = new Map<string, number>();
  private loading = new Set<string>();
  private manifest: Record<string, { files: string[]; dur?: number }> = {};
  private spatial: Voice[] = [];
  private flat: Voice[] = [];
  private last = new Map<string, number>();
  private loops = new Map<string, LoopVoice>();
  private jets = new Map<string, JetState>();
  private loopCalls = 0;
  private lx = 0; private ly = 0; private lz = 0;
  private musicLevel = 1;
  private musicDuck = 1;
  private volumeLevel = 0.9;

  /** 0..1 master level (persist it yourself) */
  set volume(v: number) { this.volumeLevel = v; if (this.ctx) this.master.gain.value = v; }
  /** 0..1 music level, separate from the master */
  set musicVolume(v: number) { this.musicLevel = v; this.applyMusic(); }
  /** Pull the music down (pause menu, big moments) without touching the player's music level. */
  duck(v: number) { this.musicDuck = v; this.applyMusic(); }
  private applyMusic() {
    if (!this.ctx) return;
    this.musicBus.gain.setTargetAtTime(this.musicLevel * this.musicDuck * 0.8, this.ctx.currentTime, 0.25);
  }

  /** Dip every non-UI sound by `amount` (0..1) and let it come back over `ms` (a hit-stop beat). */
  dip(amount: number, ms = 250) {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime, g = this.dipGain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(0.05, 1 - Math.min(1, amount)), now);
    g.setTargetAtTime(1, now + 0.02, ms / 3000);
  }

  /** A low-pass "thump": the world goes dull for a beat and swells back (big kills, close blasts). */
  thump(strength = 1) {
    const ctx = this.ctx;
    if (!ctx) return;
    const s = Math.max(0, Math.min(1, strength)), now = ctx.currentTime, f = this.thumpFilt.frequency;
    f.cancelScheduledValues(now);
    f.setValueAtTime(Math.max(250, 2400 - 2100 * s), now);
    f.setTargetAtTime(20000, now + 0.05 + 0.1 * s, 0.12 + 0.18 * s);
  }

  unlock(): void {
    if (typeof window === 'undefined') return;
    if (!this.ctx) {
      const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
      if (!AC) return;
      const ctx = this.ctx = new AC({ latencyHint: 'interactive' });
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14; comp.knee.value = 10; comp.ratio.value = 5; comp.attack.value = 0.003; comp.release.value = 0.18;
      this.master = ctx.createGain(); this.master.gain.value = this.volumeLevel;
      this.sfxBus = ctx.createGain();
      this.uiBus = ctx.createGain();
      this.dipGain = ctx.createGain();
      this.thumpFilt = ctx.createBiquadFilter(); this.thumpFilt.type = 'lowpass'; this.thumpFilt.frequency.value = 20000; this.thumpFilt.Q.value = 0.9;
      this.musicBus = ctx.createGain(); this.musicBus.gain.value = 0.8 * this.musicLevel * this.musicDuck;
      this.sfxBus.connect(this.dipGain); this.dipGain.connect(this.thumpFilt); this.thumpFilt.connect(comp);
      // UI skips the compressor (explosions mustn't pump the hit markers); a brick-wall limiter
      // on the master catches the sum instead. Ambience has its own bus: music at 0 keeps the world.
      this.ambBus = ctx.createGain(); this.ambBus.gain.value = 0.9;
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -1.5; limiter.knee.value = 0; limiter.ratio.value = 20; limiter.attack.value = 0.001; limiter.release.value = 0.12;
      this.uiBus.connect(this.master); this.ambBus.connect(this.master);
      this.musicBus.connect(this.master); comp.connect(this.master); this.master.connect(limiter); limiter.connect(ctx.destination);
      // one shared outdoor reverb (mesa slapback + dark tail); the send is per voice
      const verb = ctx.createConvolver();
      verb.buffer = outdoorIR(ctx);
      this.reverbIn = ctx.createGain(); this.reverbIn.gain.value = 0.55;
      this.reverbIn.connect(verb); verb.connect(this.sfxBus);
      for (let i = 0; i < SPATIAL_VOICES; i++) {
        const gain = ctx.createGain();
        const filt = ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 20000; filt.Q.value = 0.5;
        const pan = ctx.createPanner();
        pan.panningModel = 'equalpower'; pan.distanceModel = 'inverse';
        pan.refDistance = 4; pan.maxDistance = 180; pan.rolloffFactor = 1.0;
        const send = ctx.createGain(); send.gain.value = 0;
        gain.connect(filt); filt.connect(pan); pan.connect(this.sfxBus); pan.connect(send); send.connect(this.reverbIn);
        this.spatial.push({ src: null, gain, filt, pan, send, name: '', pri: 0, start: 0, end: 0 });
      }
      for (let i = 0; i < FLAT_VOICES; i++) {
        const gain = ctx.createGain();
        const filt = ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 20000; filt.Q.value = 0.5;
        gain.connect(filt);
        this.flat.push({ src: null, gain, filt, pan: null, send: null, name: '', pri: 0, start: 0, end: 0 });
      }
      for (const k of Object.keys(SYNTH)) { const b = synthBuffer(ctx, k); if (b) this.vars.set(k, [b]); }
      for (const k of Object.keys(SYNTH_MORE)) this.vars.set(k, synthMore(ctx, k));
      this.voice = new PopVoice(ctx, this.uiBus, (stem) => this.vars.get(stem)?.[0] ?? null);
      this.music.attach(ctx, this.musicBus, this.ambBus, baseUrl(), (stem) => this.vars.get(stem)?.[0] ?? null, (stem) => this.loadOne(stem));
      void this.loadAll();
    }
    this.music.unlockElements();
    if (this.ctx.state !== 'running') void this.ctx.resume();
  }

  private async loadOne(stem: string): Promise<void> {
    if (!this.ctx || this.vars.has(stem) || this.loading.has(stem)) return;
    this.loading.add(stem);
    try {
      const files = this.manifest[stem]?.files ?? [stem];
      const bufs = await Promise.all(files.map(async (f) => {
        try {
          const res = await fetch(baseUrl() + f + '.mp3');
          if (!res.ok) return null;
          const b = await this.ctx!.decodeAudioData(await res.arrayBuffer());
          this.onset.set(b, onsetOf(b));
          return b;
        } catch { return null; }
      }));
      const ok = bufs.filter((b): b is AudioBuffer => !!b);
      if (ok.length) this.vars.set(stem, ok);
    } finally { this.loading.delete(stem); }
  }

  private async loadAll() {
    try {
      const res = await fetch(baseUrl() + 'bank.json');
      if (res.ok) {
        const j = await res.json();
        this.manifest = j.sfx ?? {};
        this.music.setManifest(j);
      }
    } catch { /* no manifest: single files only */ }
    // combat sounds first (guns, impacts, confirms), then the rest; 6 at a time
    const stems = [...new Set(Object.entries(BANK).map(([k, d]) => d.file ?? k))].filter((s) => !this.vars.has(s));
    const first = (s: string) => (/^(shot|impact|hit|kill|crit|explosion|robot_die|player_hit|jet_)/.test(s) ? 0 : 1);
    stems.sort((a, b) => first(a) - first(b));
    const voiceStems = ['pop_a', 'pop_e', 'pop_i', 'pop_o', 'pop_u', 'pop_m', 'pop_t0', 'pop_t1', 'pop_t2'];
    const queue = [...stems, ...voiceStems];
    const worker = async () => { for (let s = queue.shift(); s; s = queue.shift()) await this.loadOne(s); };
    await Promise.all([worker(), worker(), worker(), worker(), worker(), worker()]);
  }

  private pick(pool: Voice[], name: string, pri: number, max: number, now: number): Voice | null {
    let free: Voice | null = null, same = 0, oldestSame: Voice | null = null, victim: Voice | null = null;
    for (const v of pool) {
      const busy = v.src !== null && v.end > now;
      if (!busy) { if (!free) free = v; continue; }
      if (v.name === name) { same++; if (!oldestSame || v.start < oldestSame.start) oldestSame = v; }
      if (v.pri <= pri && (!victim || v.pri < victim.pri || (v.pri === victim.pri && v.start < victim.start))) victim = v;
    }
    if (same >= max && oldestSame) return oldestSame;
    return free ?? victim;
  }

  /** a random variation, never the same one twice running */
  private variation(stem: string): AudioBuffer | null {
    const list = this.vars.get(stem);
    if (!list || !list.length) return null;
    if (list.length === 1) return list[0];
    const prev = this.lastVar.get(stem) ?? -1;
    let i = Math.floor(Math.random() * (prev < 0 ? list.length : list.length - 1));
    if (prev >= 0 && i >= prev) i++;
    this.lastVar.set(stem, i);
    return list[i];
  }

  play(name: string, pos?: { x: number; y: number; z: number }, opts?: { volume?: number; pitch?: number; lowpass?: number; delay?: number }): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const def = BANK[name];
    if (!def) return;
    const stem = def.file ?? name;
    const buf = this.variation(stem);
    if (!buf) return;
    const now = ctx.currentTime, at = now + Math.max(0, opts?.delay ?? 0);
    const gap = def.gap ?? 0.02;
    const last = this.last.get(name);
    if (last !== undefined && at - last < gap && at >= last) return;
    const spatial = !!pos && !def.ui;
    let dist = 0;
    if (spatial) {
      const dx = pos!.x - this.lx, dy = pos!.y - this.ly, dz = pos!.z - this.lz;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > MAX_HEAR * MAX_HEAR) return;
      dist = Math.sqrt(d2);
    }
    const pri = def.pri ?? 1;
    const v = this.pick(spatial ? this.spatial : this.flat, name, pri, def.max ?? 4, now);
    if (!v) return;
    this.last.set(name, at);
    // stealing a voice that is still sounding: 6 ms fade, then the new sound (no click)
    let t0 = at;
    if (v.src) {
      const old = v.src;
      if (v.end > now) {
        t0 = Math.max(at, now + 0.006);
        v.gain.gain.cancelScheduledValues(now);
        v.gain.gain.setValueAtTime(v.gain.gain.value, now);
        v.gain.gain.linearRampToValueAtTime(0, now + 0.006);
        try { old.stop(now + 0.006); } catch { /* already stopped */ }
        old.onended = () => old.disconnect();
      } else { try { old.stop(); } catch { /* already stopped */ } old.disconnect(); }
    }
    if (!spatial) { v.filt.disconnect(); v.filt.connect(def.ui ? this.uiBus : this.sfxBus); }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const rate = (def.rate ?? 1) * (opts?.pitch ?? 1) * (1 + (Math.random() * 2 - 1) * (def.pv ?? 0));
    src.playbackRate.value = rate;
    const g = def.vol * (opts?.volume ?? 1) * (1 + (Math.random() - 0.5) * 0.12);
    if (t0 === at && !(v.src && v.end > now)) v.gain.gain.cancelScheduledValues(now);
    v.gain.gain.setValueAtTime(0, t0);
    v.gain.gain.linearRampToValueAtTime(g, t0 + 0.002);
    // air absorption: far sounds lose their top end (set once, no per-frame automation)
    const air = spatial ? 1800 + 18200 * Math.pow(Math.max(0, 1 - dist / MAX_HEAR), 2.2) : 20000;
    v.filt.frequency.setValueAtTime(Math.min(air, opts?.lowpass ?? 20000), t0);
    if (v.pan && pos) this.setPan(v.pan, pos.x, pos.y, pos.z);
    if (v.send) v.send.gain.setValueAtTime((def.wet ?? 0.12) * (0.35 + 0.65 * Math.min(1, dist / 60)), t0);
    src.connect(v.gain);
    const off = this.onset.get(buf) ?? 0;
    src.start(t0, off);
    v.src = src; v.name = name; v.pri = pri; v.start = t0; v.end = t0 + (buf.duration - off) / rate;
    if (def.layer) this.play(def.layer, pos, { ...opts, delay: (opts?.delay ?? 0) + (def.layerDelay ?? 0) });
  }

  private setPan(p: PannerNode, x: number, y: number, z: number) {
    if (p.positionX) { p.positionX.value = x; p.positionY.value = y; p.positionZ.value = z; }
    else p.setPosition(x, y, z);
  }

  /**
   * A looping sound keyed by id; call every frame with its volume. Volume 0 fades it out and
   * stops it, unless `keep` (then it idles silent, so layers that come and go, like the jet's,
   * don't rebuild nodes). Params are only re-automated when they actually change.
   */
  loop(key: string, name: string, volume: number, pos?: { x: number; y: number; z: number }, pitch?: number, keep = false): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    if (++this.loopCalls % 240 === 0) for (const [k, l] of this.loops) if (l.stopAt > 0 && now >= l.stopAt) this.loops.delete(k);
    let lv = this.loops.get(key);
    if (lv && lv.stopAt > 0 && now >= lv.stopAt) { this.loops.delete(key); lv = undefined; }
    if (volume <= 0.001 && !keep) {
      if (lv && lv.stopAt === 0) this.stopLoop(lv, now, 0.05, 0.3);
      return;
    }
    const def = BANK[name];
    if (lv && (lv.name !== name || lv.stopAt > 0)) { if (lv.stopAt === 0) this.stopLoop(lv, now, 0.03, 0.2); this.loops.delete(key); lv = undefined; }
    if (!lv) {
      if (volume <= 0.001) return; // nothing to keep yet
      const stem = def?.file ?? name;
      const buf = this.vars.get(stem)?.[0];
      if (!buf) return;
      const src = ctx.createBufferSource();
      src.buffer = buf; src.loop = true;
      // loop exactly the authored length: some decoders keep the mp3 encoder padding, which
      // would otherwise be a dropout every cycle
      const off = this.onset.get(buf) ?? 0, len = this.manifest[stem]?.dur;
      if (len && off + len <= buf.duration + 0.001) { src.loopStart = off; src.loopEnd = off + len; }
      const gain = ctx.createGain(); gain.gain.value = 0;
      let pan: PannerNode | null = null;
      src.connect(gain);
      if (pos) {
        pan = ctx.createPanner();
        pan.panningModel = 'equalpower'; pan.distanceModel = 'inverse'; pan.refDistance = 4; pan.maxDistance = 180;
        gain.connect(pan); pan.connect(this.sfxBus);
      } else gain.connect(this.sfxBus);
      src.onended = () => { gain.disconnect(); pan?.disconnect(); };
      src.start(now, off + Math.random() * (len ?? buf.duration - off));
      lv = { src, gain, pan, name, stopAt: 0, vol: -1, rate: -1 };
      this.loops.set(key, lv);
    }
    const vol = (def?.vol ?? 0.5) * Math.max(0, volume), rate = pitch ?? 1;
    if (Math.abs(vol - lv.vol) > 0.004) { lv.gain.gain.setTargetAtTime(vol, now, 0.05); lv.vol = vol; }
    if (Math.abs(rate - lv.rate) > 0.004) { lv.src.playbackRate.setTargetAtTime(rate, now, 0.06); lv.rate = rate; }
    if (lv.pan && pos) this.setPan(lv.pan, pos.x, pos.y, pos.z);
  }

  private stopLoop(lv: LoopVoice, now: number, tc: number, after: number) {
    lv.gain.gain.cancelScheduledValues(now);
    lv.gain.gain.setTargetAtTime(0, now, tc);
    try { lv.src.stop(now + after); } catch { /* already stopped */ }
    lv.stopAt = now + after;
  }

  /** fade out and drop every loop and jet (leaving a game: nothing may drone on in the lobby) */
  stopAllLoops() {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const lv of this.loops.values()) if (lv.stopAt === 0) this.stopLoop(lv, now, 0.08, 0.5);
    this.loops.clear();
    this.jets.clear();
  }

  /**
   * A jetpack, driven every frame by its throttle (0 off .. 1 full afterburner), for any
   * cowboy. Three layers: the hover whine owns the low end of the throttle, the thrust roar
   * comes in above ~0.35 and climbs in pitch, and the synthetic burner rumble adds weight at
   * full burn. Lighting up fires an ignition (or a boost crack on a hard kick), cutting out a
   * short spool-down. `self` keeps your own pack slightly forward in the mix.
   */
  jet(key: string, throttle: number, pos: { x: number; y: number; z: number }, self = false) {
    if (!this.ctx) return;
    const t = Math.max(0, Math.min(1, throttle));
    let s = this.jets.get(key);
    if (!s) { s = { on: false, thr: 0, lastIgnite: -9, ref: 0, refT: 0, kh: key + ':h', kt: key + ':t', kb: key + ':b' }; this.jets.set(key, s); }
    const now = this.ctx.currentTime;
    const on = t > 0.04;
    const v = self ? 1 : 0.8;
    // the throttle is smoothed, so a hard kick shows as a rise over ~150 ms, not one frame
    if (now - s.refT > 0.15) { s.ref = s.thr; s.refT = now; }
    if (on && !s.on && now - s.lastIgnite > 0.35) { this.play('jet_ignite', pos, { volume: (0.5 + 0.5 * t) * v }); s.lastIgnite = now; }
    else if (on && t - s.ref > 0.3 && t > 0.75) { this.play('jet_boost', pos, { volume: v }); s.ref = t; }
    else if (!on && s.on) this.play('jet_cut', pos, { volume: 0.6 * v });
    s.on = on; s.thr = t;
    const hover = on ? Math.max(0, 1 - Math.max(0, t - 0.3) / 0.5) * (0.5 + 0.5 * Math.min(1, t / 0.2)) : 0;
    const thrust = on ? Math.pow(Math.max(0, (t - 0.25) / 0.75), 1.1) : 0;
    const burner = on ? Math.pow(Math.max(0, (t - 0.65) / 0.35), 1.5) : 0;
    // while lit, the layers idle silent rather than stop, so crossing a threshold never rebuilds nodes
    this.loop(s.kh, 'jet_hover_loop', hover * v, pos, 0.9 + 0.25 * t, on);
    this.loop(s.kt, 'jet_thrust_loop', thrust * v, pos, 0.85 + 0.3 * t, on);
    this.loop(s.kb, 'jetpack', burner * 0.9 * v, pos, 0.8 + 0.3 * t, on);
  }
  /** forget a jet (cowboy left / out of range) */
  jetOff(key: string) {
    const s = this.jets.get(key);
    if (!s) return;
    this.jets.delete(key);
    this.loop(s.kh, 'jet_hover_loop', 0); this.loop(s.kt, 'jet_thrust_loop', 0); this.loop(s.kb, 'jetpack', 0);
  }
  jetKeys() { return this.jets.keys(); }

  setListener(pos: THREE.Vector3 | { x: number; y: number; z: number }, forward: THREE.Vector3 | { x: number; y: number; z: number }): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.lx = pos.x; this.ly = pos.y; this.lz = pos.z;
    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.value = pos.x; l.positionY.value = pos.y; l.positionZ.value = pos.z;
      l.forwardX.value = forward.x; l.forwardY.value = forward.y; l.forwardZ.value = forward.z;
      l.upX.value = 0; l.upY.value = 1; l.upZ.value = 0;
    } else {
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(forward.x, forward.y, forward.z, 0, 1, 0);
    }
  }

  /** combat intensity 0..1: drives the island ambience's combat layer (music.ts) */
  setMusicIntensity(v: number): void { this.music.setIntensity(v); }
  /** what the score should be doing: 'saloon' | 'island' | 'boss' | 'extract' | 'victory' | 'defeat' | 'calm' | 'off' */
  setMusic(state: MusicState): void { this.music.setState(state); }
  /** a scored one-shot cue that starts on this frame, over the bed (e.g. 'sendoff' for the roof launch) */
  cue(name: 'sendoff'): void { this.music.cue(name); }

  /** Pop speaks: a per-glyph mumble matched to the typewriter (see voice.ts). Returns seconds. */
  say(text: string, tag: VoiceTag = 'neutral'): number { return this.voice?.say(text, tag) ?? 0; }

  /** lab/tests: a meter on the master output; peak() is the max |sample| since the last call */
  meter() {
    const ctx = this.ctx!;
    const an = ctx.createAnalyser(); an.fftSize = 2048;
    this.master.connect(an);
    const d = new Float32Array(an.fftSize);
    let held = 0;
    const poll = () => { an.getFloatTimeDomainData(d); for (let i = 0; i < d.length; i++) held = Math.max(held, Math.abs(d[i])); };
    const iv = setInterval(poll, 20);
    return { peak: () => { poll(); const p = held; held = 0; return p; }, stop: () => { clearInterval(iv); an.disconnect(); } };
  }
  /** lab/tests: every bank name, and what the music director is doing */
  names() { return Object.keys(BANK); }
  musicDebug() { return this.music.debug(); }

  /** Has the named sound decoded yet (lab/debug). */
  ready(name: string) { const d = BANK[name]; return this.vars.has(d?.file ?? name); }
}

/** The one sound engine, shared by the menus and the game (music keeps playing across them). */
export const SFX = new Sfx();
